import { Button } from "@babackapp/customer-ui/components/button";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { StepHeader } from "../components/step-header";
import { useAvailableResolutions } from "../hooks/use-available-resolutions";
import { useDraftReturn } from "../hooks/use-draft-return";
import { useReturnableItems } from "../hooks/use-returnable-items";
import { useUpdateLineItem } from "../hooks/use-update-line-item";
import { useWidgetParams } from "../hooks/use-widget-params";
import type { Resolution } from "../types";
import { useCustomText } from "../hooks/use-custom-text";

const resolutionSchema = z.object({
  resolution: z.enum(["refund", "exchange", "exchange-credit", "discount"] as const),
});

type ResolutionFormValues = z.infer<typeof resolutionSchema>;

export function ArticleResolutionPage() {
  const { orderId, shopId, shopSlug } = useWidgetParams();
  const t = useCustomText();

  const RESOLUTION_LABELS: Record<Resolution, string> = {
    refund: t("resolution.refund.title"),
    exchange: t("resolution.exchange.title"),
    "exchange-credit": t("resolution.exchangeCredit.title"),
    discount: t("resolution.discount.title"),
  };

  const RESOLUTION_DESCRIPTIONS: Record<Resolution, string> = {
    refund: t("resolution.refund.descriptionShort"),
    exchange: t("resolution.exchange.descriptionShort"),
    "exchange-credit": t("resolution.exchangeCredit.description"),
    discount: t("resolution.discount.description"),
  };
  const { lineItemId } = useParams<{ lineItemId: string }>();
  const navigate = useNavigate();
  const draft = useDraftReturn(orderId);
  const updateLineItem = useUpdateLineItem(orderId);
  const items = useReturnableItems(orderId, shopSlug);

  const currentLineItemId = lineItemId ?? "";
  const currentItem = items.find((i) => i.line_item_id === currentLineItemId);
  const currentLineItemTitle = currentItem?.title ?? currentLineItemId;

  const availableResolutions = useAvailableResolutions(shopId, currentItem!);
  const draftItem = draft?.lineItems.find((li) => li.lineItemId === currentLineItemId);

  const form = useForm<ResolutionFormValues>({
    resolver: zodResolver(resolutionSchema),
    defaultValues: {
      resolution: (draftItem?.resolution as Resolution | undefined) ?? undefined,
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    updateLineItem(currentLineItemId, { resolution: values.resolution, resolutionOption: null });
    navigate("../../add-other-article");
  });

  const selected = form.watch("resolution");

  return (
    <WidgetPage>
      <StepHeader
        title={currentLineItemTitle}
        description={t("articleResolution.description")}
      />
      <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4">
        <div className="flex flex-1 flex-col overflow-y-auto">
          <Controller
            control={form.control}
            name="resolution"
            render={({ field }) => (
              <OptionsList>
                {availableResolutions.map((resolution) => (
                  <Option
                    key={resolution}
                    data-state={field.value === resolution ? "checked" : "unchecked"}
                    onClick={() => field.onChange(resolution)}
                  >
                    <OptionTitle>{RESOLUTION_LABELS[resolution]}</OptionTitle>
                    <OptionDescription>{RESOLUTION_DESCRIPTIONS[resolution]}</OptionDescription>
                    <OptionAction>
                      {field.value === resolution && <CheckIcon className="text-primary" />}
                    </OptionAction>
                  </Option>
                ))}
              </OptionsList>
            )}
          />
        </div>
        <div className="mt-auto flex gap-2 pb-4 shrink-0">
          <Button type="button" variant="outline" size="lg" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
          <Button type="submit" size="lg" className="flex-1" disabled={!selected}>
            {t("common.continue")}
          </Button>
        </div>
      </form>
    </WidgetPage>
  );
}
